import { useState } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Building2, MoreHorizontal } from 'lucide-react'
import { usePostHog } from '@posthog/react'
import {
  accountKeys,
  accountsQueryOptions,
  createAccount,
  deleteAccount,
  updateAccount,
} from '#/api/accounts'
import type { Account } from '#/api/accounts'
import { orgSettingsQueryOptions } from '#/api/org-settings'
import { Button } from '#/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/components/ui/dropdown-menu'
import { accountStatusTone } from '#/lib/status-styles'
import { initials } from '#/lib/initials'
import { LinkCard } from '#/components/link-card'
import { StatusMark } from '#/components/status-mark'
import { NameDialog } from '#/components/name-dialog'
import { DeleteConfirm } from '#/components/delete-confirm'
import { PageHeader } from '#/components/page-header'
import { EmptyState } from '#/components/empty-state'
import { RoutePending } from '#/components/route-pending'
import { RouteError } from '#/components/route-error'
import { hasPermission, useCan } from '#/user/me'
import { PageContainer } from '#/components/page-container'
import { accountName } from '#/lib/display-name'

export const Route = createFileRoute('/_authed/accounts/')({
  loader: ({ context }) =>
    // The account limit (/org/settings) is only needed by users who can create
    // accounts — they see the usage indicator + "New account" button. The BFF
    // gates that endpoint on annwn:accounts:read (held by every role), so this
    // prefetch is scoped to accounts:manage purely because only managers use the
    // limit; a plain member reads the account list without it.
    hasPermission(context.me, 'annwn:accounts:manage')
      ? context.queryClient.ensureQueryData(orgSettingsQueryOptions)
      : undefined,
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: AccountsList,
})

type DialogState =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'rename'; account: Account }
  | { kind: 'delete'; account: Account }

function AccountsList() {
  const { t } = useTranslation()
  const { data: accounts } = useSuspenseQuery(accountsQueryOptions)
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' })
  const router = useRouter()
  const queryClient = useQueryClient()
  const posthog = usePostHog()
  const canManage = useCan('annwn:accounts:manage')

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: accountKeys.all })

  const createMutation = useMutation({
    mutationFn: (input: { name: string }) => createAccount(input),
    onSuccess: async (res, input) => {
      await invalidate()
      setDialog({ kind: 'closed' })
      posthog.capture('account_created', {
        account_id: res.uuid,
        account_name: input.name,
      })
      void router.navigate({
        to: '/accounts/$accountId/workspaces',
        params: { accountId: res.uuid },
      })
    },
  })

  const renameMutation = useMutation({
    mutationFn: ({ uuid, name }: { uuid: string; name: string }) =>
      updateAccount(uuid, { name }),
    onSuccess: async () => {
      await invalidate()
      setDialog({ kind: 'closed' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => deleteAccount(uuid),
    onSuccess: async (_, uuid) => {
      await invalidate()
      setDialog({ kind: 'closed' })
      posthog.capture('account_deleted', { account_id: uuid })
    },
  })

  return (
    <PageContainer>
      <PageHeader
        title={t('accounts.title')}
        actions={
          canManage ? (
            <AccountCreateAction
              usedCount={accounts.length}
              onCreate={() => setDialog({ kind: 'create' })}
            />
          ) : null
        }
      />

      {accounts.length === 0 ? (
        <EmptyState
          icon={Building2}
          title={t('accounts.empty')}
          body={t('accounts.emptyBody')}
        />
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {accounts.map((account) => (
            <AccountCard
              key={account.uuid}
              account={account}
              canManage={canManage}
              onRename={() => setDialog({ kind: 'rename', account })}
              onDelete={() => setDialog({ kind: 'delete', account })}
            />
          ))}
        </ul>
      )}

      <NameDialog
        open={dialog.kind === 'create'}
        onOpenChange={(o) =>
          setDialog(o ? { kind: 'create' } : { kind: 'closed' })
        }
        title={t('accounts.new')}
        label={t('accounts.fields.name')}
        placeholder={t('accounts.fields.namePlaceholder')}
        submitLabel={t('common.create')}
        busy={createMutation.isPending}
        error={createMutation.isError}
        onSubmit={(name) => createMutation.mutate({ name })}
      />

      <NameDialog
        open={dialog.kind === 'rename'}
        onOpenChange={(o) => {
          if (!o) setDialog({ kind: 'closed' })
        }}
        title={t('accounts.rename')}
        label={t('accounts.fields.name')}
        placeholder={t('accounts.fields.namePlaceholder')}
        initialName={
          dialog.kind === 'rename' ? (dialog.account.name ?? '') : ''
        }
        submitLabel={t('common.save')}
        busy={renameMutation.isPending}
        error={renameMutation.isError}
        onSubmit={(name) => {
          if (dialog.kind !== 'rename') return
          renameMutation.mutate({ uuid: dialog.account.uuid, name })
        }}
      />

      <DeleteConfirm
        open={dialog.kind === 'delete'}
        onOpenChange={(o) => {
          if (!o) setDialog({ kind: 'closed' })
        }}
        title={t('accounts.deleteTitle')}
        description={t('accounts.deleteDescription', {
          name: dialog.kind === 'delete' ? accountName(t, dialog.account) : '',
        })}
        busy={deleteMutation.isPending}
        onConfirm={() => {
          if (dialog.kind !== 'delete') return
          deleteMutation.mutate(dialog.account.uuid)
        }}
      />
    </PageContainer>
  )
}

// The account-usage indicator + "New account" button. Extracted so the org
// settings query (the account limit) is fetched ONLY when the user can manage
// accounts — they're the only ones who need the limit. A viewer without
// annwn:accounts:manage never mounts this, so /org/settings is never called and
// the accounts list stays readable for them.
function AccountCreateAction({
  usedCount,
  onCreate,
}: {
  usedCount: number
  onCreate: () => void
}) {
  const { t } = useTranslation()
  const { data: orgSettings } = useSuspenseQuery(orgSettingsQueryOptions)
  const limit = orgSettings.account_limit
  const atLimit = usedCount >= limit
  return (
    <>
      <span className="text-body-sm text-muted-foreground tabular-nums">
        {t('accounts.usage', { used: usedCount, limit })}
      </span>
      <Button
        type="button"
        disabled={atLimit}
        onClick={onCreate}
        title={atLimit ? t('accounts.atLimit') : undefined}
      >
        {t('accounts.new')}
      </Button>
    </>
  )
}

// An account as the same card as a workspace: an initials tile and the name in
// the display face, with a quiet footer. Accounts are active as a rule, so the
// footer only names a status that is the exception.
function AccountCard({
  account,
  canManage = true,
  onRename,
  onDelete,
}: {
  account: Account
  canManage?: boolean
  onRename: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation()
  const tone = accountStatusTone[account.status]
  return (
    <li>
      <LinkCard
        link={{
          to: '/accounts/$accountId/dashboard',
          params: { accountId: account.uuid },
        }}
        title={
          <span className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted font-serif text-heading-lg text-title"
            >
              {initials(account.name)}
            </span>
            <span className="min-w-0 truncate font-serif text-display-sm text-title">
              {accountName(t, account)}
            </span>
          </span>
        }
        status={
          tone ? (
            <StatusMark tone={tone}>
              {t(`accounts.status.${account.status}`)}
            </StatusMark>
          ) : null
        }
        actions={
          canManage ? (
            <AccountCardMenu onRename={onRename} onDelete={onDelete} />
          ) : null
        }
        className="min-h-36 gap-4"
      />
    </li>
  )
}

function AccountCardMenu({
  onRename,
  onDelete,
}: {
  onRename: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t('common.more')}>
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onRename}>
          {t('accounts.rename')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onDelete} variant="destructive">
          {t('common.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
